import { Link } from "react-router-dom";
import { Building2, MapPin, Share2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { areaText, inrCompact } from "@/lib/format";
import { StatusBadge, UserAvatar } from "@/components/common";
import { shareProperty } from "./share";
import { useBranding } from "@/hooks/useData";

export const priceLabel = (p) => `${inrCompact(p.price)}${p.listing_type === "Rent" ? "/mo" : ""}`;

export function PropertyThumb({ p, className = "h-44" }) {
  const ph = p.photos?.[0];
  return ph ? (
    <img src={fileUrl(ph.path)} alt={p.project} loading="lazy" className={`${className} w-full object-cover`} />
  ) : (
    <div className={`${className} flex w-full items-center justify-center bg-secondary text-muted-foreground`}><Building2 className="h-10 w-10" /></div>
  );
}

const ShareBtn = ({ p }) => {
  const { data: brand } = useBranding();
  return (
  <button
    data-testid={`property-whatsapp-share-${p.id}`}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); shareProperty(p, brand?.company_name); }}
    className="flex h-8 w-8 items-center justify-center rounded-full border text-[#25D366] transition-colors hover:bg-secondary"
    aria-label="Share on WhatsApp"
  >
    <Share2 className="h-4 w-4" />
  </button>
  );
};

export function PropertyCard({ p, owner }) {
  return (
    <Link to={`/properties/${p.id}`} data-testid={`property-card-${p.id}`} className="group overflow-hidden rounded-2xl border bg-card transition-colors hover:border-primary/40">
      <div className="relative overflow-hidden">
        <PropertyThumb p={p} />
        <span className="absolute left-3 top-3 rounded-md bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur">{p.listing_type}</span>
        <span className="absolute right-3 top-3"><StatusBadge status={p.status} /></span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="num text-xl font-semibold">{priceLabel(p)}</p>
          <ShareBtn p={p} />
        </div>
        <p className="mt-1 truncate font-semibold">{p.property_type}{p.bhk ? ` · ${p.bhk}` : ""}</p>
        <p className="truncate text-sm text-muted-foreground">{p.project}</p>
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" />{[p.sector, p.city].filter(Boolean).join(", ")}</span>
          <span className="shrink-0">{areaText(p.area, p.area_unit)}</span>
        </div>
        {owner && <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><UserAvatar user={owner} />{owner.name}</div>}
      </div>
    </Link>
  );
}

export function PropertyRow({ p, owner }) {
  return (
    <Link to={`/properties/${p.id}`} data-testid={`property-row-${p.id}`} className="flex items-center gap-4 rounded-xl border bg-card p-3 transition-colors hover:border-primary/40">
      <div className="h-16 w-20 shrink-0 overflow-hidden rounded-lg"><PropertyThumb p={p} className="h-16" /></div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{p.property_type}{p.bhk ? ` · ${p.bhk}` : ""} <span className="font-normal text-muted-foreground">· {p.listing_type}</span></p>
        <p className="truncate text-xs text-muted-foreground">{p.project} · {[p.sector, p.city].filter(Boolean).join(", ")}</p>
        <div className="mt-1 flex items-center gap-2"><StatusBadge status={p.status} />{owner && <UserAvatar user={owner} />}</div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <span className="num text-sm font-semibold">{priceLabel(p)}</span>
        <span className="hidden text-xs text-muted-foreground sm:block">{areaText(p.area, p.area_unit)}</span>
        <ShareBtn p={p} />
      </div>
    </Link>
  );
}
